/**
 * Pure state-manipulation helpers for the SwatchPanel. The shell owns the
 * selection map; SwatchPanel + Swatch components are stateless renderers
 * that call into these helpers via the `onSelect` callback.
 *
 * Selection model: one active swatch per palette (selecting another swatch
 * in the same palette replaces). Selecting the currently-active swatch
 * toggles it off — useful for "no swatch" recipes where the operation
 * picks a default.
 */

import type {
  StudioMetadataSchema,
  StudioParameterPalette,
  StudioSwatch,
} from '../../../domain';

/**
 * Map of paletteId -> active swatchId. A palette absent from the map has
 * no active swatch.
 */
export type SwatchSelections = Record<string, string>;

/**
 * Toggle / replace the active swatch for a palette. Returns a new map.
 *
 * Behavior:
 * - If `swatchId` is the same as the currently-active one for the palette,
 *   the palette's entry is REMOVED (toggle-off).
 * - Otherwise the palette's entry is replaced with the new swatchId.
 * - Other palettes are untouched.
 */
export function toggleSwatchSelection(
  prev: SwatchSelections,
  paletteId: string,
  swatchId: string,
): SwatchSelections {
  if (prev[paletteId] === swatchId) {
    const { [paletteId]: _removed, ...rest } = prev;
    void _removed;
    return rest;
  }
  return { ...prev, [paletteId]: swatchId };
}

/**
 * Resolve the active swatch object for a palette given a selection map.
 * Returns `null` when no swatch is selected, or when the selected swatchId
 * isn't in the palette anymore (defensive — palettes can change between
 * sessions; a stale id just renders as "no selection").
 */
export function resolveActiveSwatch(
  palette: StudioParameterPalette,
  selections: SwatchSelections,
): StudioSwatch | null {
  const activeId = selections[palette.id];
  if (!activeId) return null;
  return palette.swatches.find(s => s.id === activeId) ?? null;
}

/**
 * Project the active selections into a flat record of palette.kind -> value
 * for operation requests. For example a `color` palette with active swatch
 * value `"#ff0044"` becomes `{ color: "#ff0044" }`. Useful when forwarding
 * selections into AdditionalParameters or the prompt enrichment step.
 *
 * Currently NOT consumed by F14 — included so the F14b/F-T integration
 * landings have one canonical shape. The shape mirrors v1's flat-record
 * convention for fashion params.
 */
export function selectionsToParameterRecord(
  palettes: StudioParameterPalette[],
  selections: SwatchSelections,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const palette of palettes) {
    const active = resolveActiveSwatch(palette, selections);
    if (active) {
      out[palette.kind] = active.value;
    }
  }
  return out;
}

/**
 * Humanized description of the active selections, suitable for appending
 * to an operation prompt. Skips palettes with no active swatch and
 * palettes whose selection is stale (id not in palette anymore).
 *
 * Format: `"palette name: swatch label, palette name: swatch label"` —
 * comma-separated, lowercase palette name, original-case swatch label.
 *
 * Example:
 *   palettes:    [{name:'Fabric',swatches:[{id:'a',label:'Linen'}]}, ...]
 *   selections:  { fabric-palette: 'a', color-palette: 'red' }
 *   returns:     "fabric: Linen, color: Coral Red"
 *
 * Returns an empty string when no selections are active.
 */
export function describeSwatches(
  palettes: StudioParameterPalette[],
  selections: SwatchSelections,
): string {
  const items: string[] = [];
  for (const palette of palettes) {
    const active = resolveActiveSwatch(palette, selections);
    if (!active) continue;
    items.push(`${palette.name.toLowerCase()}: ${active.label}`);
  }
  return items.join(', ');
}

/**
 * Merge a swatch description into an operation prompt. Format is
 * `"<prompt> [<description>]"` — square-bracketed so the user can see
 * exactly what's being added, and downstream models tend to weight
 * bracketed phrases as additive constraints.
 *
 * - Empty description -> prompt unchanged.
 * - Empty prompt + non-empty description -> description alone (for
 *   ops where the prompt is optional, the swatches still seed something
 *   for the model to ground on).
 * - Both empty -> empty string.
 */
export function mergePromptWithSwatches(prompt: string, description: string): string {
  const p = prompt.trim();
  const d = description.trim();
  if (!d) return p;
  if (!p) return d;
  return `${p} [${d}]`;
}

/**
 * Structured projection of the active selections into the values an
 * operation request actually consumes. Classifies by `palette.kind`:
 *
 * - `style` and `studio-filter` swatches contribute their `value`
 *   (a prompt fragment) to `promptFragments`, which prompt-having
 *   panels append to the user's prompt.
 * - `aspect-ratio` swatches parse `value` (shape `"WIDTHxHEIGHT"`)
 *   into a `{width, height}` pair on `aspectRatio` that overrides
 *   the Generate panel's w/h form fields.
 * - Every active swatch (including unrecognized kinds) contributes
 *   its `label` to `chipLabels` for the UI's "Studio: …" chip row.
 * - Unrecognized kinds also feed into `bracketDescription` (the F14b
 *   format `"kind: label, kind: label"`) for back-compat with the
 *   pre-Bucket-2 prompt-merge behavior used by other Studios whose
 *   palettes pre-date the recognized-kind taxonomy.
 *
 * Recognized kinds match the palette `Kind` values seeded for
 * Generic Studio in `StudioSeedData.cs` (B-GS-palettes).
 */
export interface StudioParams {
  promptFragments: string[];
  aspectRatio: { width: number; height: number } | null;
  chipLabels: string[];
  bracketDescription: string;
}

const STRUCTURED_PROMPT_KINDS = new Set(['style', 'studio-filter']);
const ASPECT_RATIO_KIND = 'aspect-ratio';

function parseDimensions(value: string): { width: number; height: number } | null {
  // Accept "WxH" (case-insensitive separator). Strict parse — both sides
  // must be positive finite integers.
  const m = value.trim().match(/^(\d+)\s*[xX]\s*(\d+)$/);
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return { width: w, height: h };
}

export function studioParamsFromSelections(
  palettes: StudioParameterPalette[],
  selections: SwatchSelections,
): StudioParams {
  const promptFragments: string[] = [];
  let aspectRatio: { width: number; height: number } | null = null;
  const chipLabels: string[] = [];
  const bracketParts: string[] = [];

  for (const palette of palettes) {
    const active = resolveActiveSwatch(palette, selections);
    if (!active) continue;
    chipLabels.push(active.label);

    if (STRUCTURED_PROMPT_KINDS.has(palette.kind)) {
      const v = active.value.trim();
      if (v) promptFragments.push(v);
    } else if (palette.kind === ASPECT_RATIO_KIND) {
      // Prefer Metadata.width/height if present (the seed populates both as
      // belt-and-suspenders), else fall back to parsing Value.
      const meta = active.metadata ?? undefined;
      const w = meta?.width ? Number(meta.width) : NaN;
      const h = meta?.height ? Number(meta.height) : NaN;
      if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
        aspectRatio = { width: w, height: h };
      } else {
        const parsed = parseDimensions(active.value);
        if (parsed) aspectRatio = parsed;
      }
    } else {
      // Unrecognized kind — fall through to the F14b bracket-describe path.
      bracketParts.push(`${palette.name.toLowerCase()}: ${active.label}`);
    }
  }

  return {
    promptFragments,
    aspectRatio,
    chipLabels,
    bracketDescription: bracketParts.join(', '),
  };
}

/**
 * Append a list of prompt fragments to a user prompt as comma-separated
 * tokens. Used by GeneratePanel + ImageToImagePanel to push the active
 * Studio's style + studio-filter swatch values into the request.
 *
 * - Empty fragments list -> prompt unchanged.
 * - Empty prompt + non-empty fragments -> just the fragments (so a
 *   user who only sets swatches and hits Run still has something for
 *   the model to ground on — same affordance as the F14b bracket path).
 * - Both empty -> empty string.
 */
export function mergePromptWithFragments(prompt: string, fragments: string[]): string {
  const p = prompt.trim();
  const frag = fragments.map(f => f.trim()).filter(Boolean).join(', ');
  if (!frag) return p;
  if (!p) return frag;
  return `${p}, ${frag}`;
}

/**
 * Build the studio-typed metadata block to persist with an operation
 * (Path C Slice 6, write side). For each field in the studio's
 * `metadataSchema`, find the palette it draws from (`field.sourcePaletteId`),
 * read the active swatch, and record the swatch **id** — which the backend's
 * schema `allowedValues` are defined against (seed convention: swatch id
 * `"ankara"` ↔ allowed value `"ankara"`).
 *
 * Returns `{ [schema.metadataKey]: { <field>: <swatchId>, … } }` — keyed by the
 * schema's authoritative `metadataKey` (underscored, e.g. `studio_fashion`),
 * NOT the studio id. Returns `undefined` when there's no schema or no mapped
 * selection, so callers simply omit `metadata` from the request.
 *
 * Only fields with a `sourcePaletteId` that resolves to an active swatch are
 * included; unknown fields are never sent (the BE drops them unless the schema
 * sets `rejectUnknownFields`).
 */
export function buildStudioMetadata(
  schema: StudioMetadataSchema | undefined,
  palettes: StudioParameterPalette[],
  selections: SwatchSelections,
): Record<string, unknown> | undefined {
  if (!schema?.fields?.length || !schema.metadataKey) return undefined;
  const block: Record<string, string> = {};
  for (const field of schema.fields) {
    if (!field.sourcePaletteId) continue;
    const palette = palettes.find(p => p.id === field.sourcePaletteId);
    if (!palette) continue;
    const active = resolveActiveSwatch(palette, selections);
    if (active) block[field.name] = active.id;
  }
  if (Object.keys(block).length === 0) return undefined;
  return { [schema.metadataKey]: block };
}
